const containsIgnoreCase = (candidate, query) => candidate.toLowerCase().indexOf(query.toLowerCase()) > -1;
export const PROFILE_DISPLAY_NAME_MAX_LENGTH = 30;
/** Public sentinel shown for soft-deleted authors (API + clients). */
export const DELETED_AUTHOR_DISPLAY_NAME = 'Deleted author';
export const DELETED_AUTHOR_HANDLE = 'deleted';
export const clampProfileDisplayName = (displayName) => {
    if (!displayName)
        return undefined;
    const clamped = displayName.slice(0, PROFILE_DISPLAY_NAME_MAX_LENGTH);
    return clamped || undefined;
};
export const isDeletedProfile = (profile) => !!(profile === null || profile === void 0 ? void 0 : profile.deletedAt);
/**
 * Strip PII from a soft-deleted profile for public API / UI surfaces.
 * Keeps id + timestamps (including deletedAt) so clients can detect the state.
 */
export const toPublicDeletedProfile = (profile) => ({
    ...profile,
    displayName: DELETED_AUTHOR_DISPLAY_NAME,
    handle: DELETED_AUTHOR_HANDLE,
    avatar: null,
    header: null,
    bio: undefined,
    location: undefined,
    locked: undefined,
    bot: undefined,
    discoverable: undefined,
    memberships: [],
    fields: undefined,
    profileStats: undefined,
    pinnedPostId: undefined,
    roles: [],
});
export const anonymizeProfileIfDeleted = (profile) => {
    if (!profile)
        return undefined;
    return isDeletedProfile(profile) ? toPublicDeletedProfile(profile) : profile;
};
/**
 * Tombstone for internal `ProfileWithMeta` surfaces (e.g. moderation reports).
 * Built explicitly rather than by spreading the original so no PII of the
 * deleted account (email/controllers, social graph, time zone, guest data)
 * can leak, while still satisfying `profileWithMetaSchema` — unlike
 * {@link toPublicDeletedProfile}, whose stripped shape is only valid for the
 * looser `publicProfileSchema`.
 */
export const toDeletedProfileWithMeta = (profile) => ({
    id: profile.id,
    type: profile.type,
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt,
    deletedAt: profile.deletedAt,
    handle: DELETED_AUTHOR_HANDLE,
    displayName: DELETED_AUTHOR_DISPLAY_NAME,
    avatar: null,
    header: null,
    roles: [],
    followers: [],
    following: [],
    controllers: [],
    memberships: [],
    profileStats: { followersCount: 0, followingCount: 0 },
    blockingIds: [],
    blockedByIds: [],
    followedHashtags: [],
});
/** Tombstone a `ProfileWithMeta` only when it is soft-deleted. */
export const tombstoneProfileWithMetaIfDeleted = (profile) => profile && isDeletedProfile(profile)
    ? toDeletedProfileWithMeta(profile)
    : profile;
export const profileName = (profile) => (profile === null || profile === void 0 ? void 0 : profile.displayName) || (profile === null || profile === void 0 ? void 0 : profile.handle);
export const matchesQuery = (profile, query, queryOptions = { handle: true, displayName: true }) => !!(profile &&
    (query.startsWith('@')
        ? profile.handle && containsIgnoreCase(profile.handle, query.substring(1))
        : (queryOptions.handle &&
            profile.handle &&
            containsIgnoreCase(profile.handle, query)) ||
            (queryOptions.displayName &&
                profile.displayName &&
                containsIgnoreCase(profile.displayName, query))));
export const inviteLinkMatchesQuery = (inviteLink, query, queryOptions = { handle: true, displayName: true, slug: true, group: true }) => {
    const profile = inviteLink.profile;
    if (query.startsWith('@')) {
        return !!((profile === null || profile === void 0 ? void 0 : profile.handle) && containsIgnoreCase(profile.handle, query.substring(1)));
    }
    const groupMatches = queryOptions.group &&
        inviteLink.groups.some((group) => containsIgnoreCase(group.handle, query) ||
            (group.displayName && containsIgnoreCase(group.displayName, query)));
    return !!(groupMatches ||
        (profile &&
            ((queryOptions.handle &&
                profile.handle &&
                containsIgnoreCase(profile.handle, query)) ||
                (queryOptions.displayName &&
                    profile.displayName &&
                    containsIgnoreCase(profile.displayName, query)) ||
                (queryOptions.slug &&
                    inviteLink.slug &&
                    containsIgnoreCase(inviteLink.slug, query)))));
};
export const sortProfiles = (profiles) => {
    var _a;
    return (_a = profiles === null || profiles === void 0 ? void 0 : profiles.sort((a, b) => profileName(a).localeCompare(profileName(b)))) !== null && _a !== void 0 ? _a : [];
};
export const isStripeActive = (sub) => (sub === null || sub === void 0 ? void 0 : sub.status) === 'active' || (sub === null || sub === void 0 ? void 0 : sub.status) === 'trialing';
export const getStripeCustomerId = (profileSettings) => { var _a; return (_a = profileSettings === null || profileSettings === void 0 ? void 0 : profileSettings.stripeSettings) === null || _a === void 0 ? void 0 : _a.customerId; };
export const isOwnerProfile = (profile) => {
    return profile === null || profile === void 0 ? void 0 : profile.roles.some((role) => role.key === 'owner');
};
export const blockedPairIds = (profile) => {
    var _a, _b;
    return [
        ...new Set([
            ...((_a = profile === null || profile === void 0 ? void 0 : profile.blockingIds) !== null && _a !== void 0 ? _a : []),
            ...((_b = profile === null || profile === void 0 ? void 0 : profile.blockedByIds) !== null && _b !== void 0 ? _b : []),
        ]),
    ];
};
export const isBlockedPair = (profile, otherId) => !!otherId && blockedPairIds(profile).includes(otherId);
export const viewerBlockedTarget = (profile, otherId) => { var _a; return !!otherId && ((_a = profile === null || profile === void 0 ? void 0 : profile.blockingIds) !== null && _a !== void 0 ? _a : []).includes(otherId); };
export const targetBlockedViewer = (profile, otherId) => { var _a; return !!otherId && ((_a = profile === null || profile === void 0 ? void 0 : profile.blockedByIds) !== null && _a !== void 0 ? _a : []).includes(otherId); };
export const sharesControllingAccount = (a, b) => {
    var _a, _b;
    const otherIds = new Set(((_a = b.controllers) !== null && _a !== void 0 ? _a : []).map((account) => account.id));
    return ((_b = a.controllers) !== null && _b !== void 0 ? _b : []).some((account) => otherIds.has(account.id));
};
export const toBlockedProfileStub = (profile) => ({
    id: profile.id,
    type: profile.type,
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt,
    handle: profile.handle,
    displayName: profile.displayName,
    blockedByMe: true,
});
